import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OrderDriver from '../orderdriver/OrderDriver';
import OrderListHeader from '../orderslist/OrderListHeader';
import OrderItem from '../orderslist/OrderItem';
import NextOrderFooter from '../orderslist/NextOrderFooter';

function OrderList({ setActiveComponent, userId }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderPoints, setOrderPoints] = useState([]);
  const [driver, setDriver] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeStarted, setRouteStarted] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchOrderPoints();
      fetchDriverData();
    }
  }, [userId]);

  const fetchDriverData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/location/driver`, {
        params: { driver_id: userId },
      });
      setDriver(response.data);
      console.log('Fetched driver:', response.data);
    } catch (error) {
      console.error('Error fetching driver:', error);
    }
  };

  const fetchOrderPoints = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/delivery_orders/driver/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrderPoints(response.data);
      
      // Check if route has been started - only consider non-submitted orders
      const hasStartedRoute = response.data.some(
        o => !o.is_submitted && o.status && o.status.toLowerCase() !== 'pending'
      );
      setRouteStarted(hasStartedRoute);
      
      console.log('Fetched order points:', response.data);
    } catch (error) {
      console.error('Error fetching order points:', error);
    }
  };

  // Custom function to handle going back from OrderDriver
  const handleBackFromOrder = () => {
    setSelectedOrder(null);
    fetchOrderPoints(); // Refresh the list
  };

  const handlePickupRoutes = async () => {
    if (loadingRoutes || !driver) return;

    // Filter out submitted orders and only get pending ones
    const pendingOrders = orderPoints.filter(
      o => o.status === 'pending' && !o.is_submitted
    );

    if (pendingOrders.length === 0) {
      alert('No hay recojos pendientes para procesar.');
      return;
    }

    const sortedOrders = pendingOrders.sort((a, b) => a.sequence - b.sequence);

    // Validate driver coordinates
    if (!driver.latitude || !driver.longitude) {
      alert('No se pudo obtener tu ubicación. Por favor intenta de nuevo.');
      return;
    }

    // Validate order coordinates
    const invalidOrders = sortedOrders.filter(
      o => !o.latitude || !o.longitude
    );
    if (invalidOrders.length > 0) {
      alert('Algunos recojos no tienen coordenadas válidas.');
      console.error('Invalid orders:', invalidOrders);
      return;
    }

    const startPoint = {
      lat: driver.latitude,
      lng: driver.longitude,
    };

    const endPoint = {
      lat: sortedOrders[sortedOrders.length - 1].latitude,
      lng: sortedOrders[sortedOrders.length - 1].longitude,
    };

    const stops = sortedOrders.slice(0, -1).map(order => ({
      lat: order.latitude,
      lng: order.longitude,
    }));

    const orderIds = sortedOrders.map(order => order.id);

    setLoadingRoutes(true);

    try {
      await createGoogleRoute(startPoint, endPoint, stops, orderIds);
      await fetchOrderPoints(); // Refresh the list
      setRouteStarted(true);
      alert('¡Empezaste tu ruta de recojos, suerte!');
    } catch (error) {
      console.error('Error updating arrival times:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Algo falló, por favor intenta de nuevo.';
      alert(errorMessage);
    } finally {
      setLoadingRoutes(false);
    }
  };

  const createGoogleRoute = async (startPoint, endPoint, waypoints, orderIds) => {
    const token = localStorage.getItem('token');

    // Only format waypoints if there are any
    const formattedWaypoints = waypoints.length > 0
      ? waypoints.map(wp => `${wp.lat},${wp.lng}`).join('|')
      : '';

    console.log('Creating pickup route with:', {
      startPoint: `${startPoint.lat},${startPoint.lng}`,
      endPoint: `${endPoint.lat},${endPoint.lng}`,
      waypoints: formattedWaypoints,
      orderIds,
      waypointCount: waypoints.length
    });

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/create_route`,
      {
        startPoint: `${startPoint.lat},${startPoint.lng}`,
        endPoint: `${endPoint.lat},${endPoint.lng}`,
        waypoints: formattedWaypoints,
        orderIds: orderIds,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('Route creation response:', response.data);

    if (response.data.message !== 'Arrival times updated successfully') {
      throw new Error('Failed to create route with Google Maps API');
    }

    return response.data;
  };

  // Separate orders into active and completed
  const activeOrders = orderPoints
    .filter(o => !o.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  const completedOrders = orderPoints
    .filter(o => o.is_submitted)
    .sort((a, b) => a.sequence - b.sequence);

  // Find next order (first active order in sequence)
  const nextOrder = activeOrders[0];

  // Check if all non-submitted orders are pending
  const nonSubmittedOrders = orderPoints.filter(o => !o.is_submitted);
  const allPending = nonSubmittedOrders.every(o => o.status === 'pending');
  const canStartRoute = allPending && nonSubmittedOrders.length > 0 && !routeStarted;

  if (selectedOrder) {
    return (
      <OrderDriver
        order={selectedOrder}
        onBack={handleBackFromOrder}
        setActiveComponent={setActiveComponent}
      />
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <OrderListHeader
        setActiveComponent={setActiveComponent}
        onStartRoute={handlePickupRoutes}
        canStartRoute={canStartRoute}
        loadingRoutes={loadingRoutes}
        routeStarted={routeStarted}
      />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-24">
        <h1 className="text-3xl font-montserrat font-bold text-brand-primary px-5 py-4">
          Recojos
        </h1>

        {/* Active Orders */}
        {activeOrders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Activos
            </h2>
            <div className="bg-white">
              {activeOrders.map((order, index) => (
                <OrderItem
                  key={order.id}
                  order={order}
                  onClick={() => setSelectedOrder(order)}
                  isLast={index === activeOrders.length - 1}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed Orders */}
        {completedOrders.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 px-5 py-2">
              Completados
            </h2>
            <div className="bg-white opacity-60">
              {completedOrders.map((order, index) => (
                <OrderItem
                  key={order.id}
                  order={order}
                  onClick={() => {}}
                  isLast={index === completedOrders.length - 1}
                  disabled={true}
                />
              ))}
            </div>
          </div>
        )}

        {orderPoints.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay recojos asignados
          </div>
        )}
      </div>

      {/* Fixed Footer - Next Order Quick Access */}
      {nextOrder && (
        <NextOrderFooter
          order={nextOrder}
          onClick={() => setSelectedOrder(nextOrder)}
        />
      )}
    </div>
  );
}

export default OrderList;